/**
 * HRD Login Page wrapper for Portal.
 *
 * Wraps the shared @platform-ui/auth HrdLogin component with portal-specific
 * behaviors like handling MSAL in-progress state and redirecting authenticated users.
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { HrdLogin } from '@platform-ui/auth/react';
import { consumePostLoginPath } from '@platform-ui/auth';
import { defaultAuthority, domainMappings, isAuthDisabled } from './auth-config';
import { authService } from './auth-service';
import { Spinner } from '@aero/ui';
import { env } from '@/config/env';

const STORAGE_PREFIX = 'portal-auth';

/**
 * Loading screen shown while MSAL is processing auth state.
 */
function AuthCheckingScreen() {
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                height: '100%',
                backgroundColor: 'rgba(255,255,255,0.4)'
            }}>
            <Spinner className="size-4" />
            <p style={{ color: '#64748b', fontSize: '1rem' }}>Checking authentication...</p>
        </div>
    );
}

/**
 * Portal HRD Login Page.
 *
 * This page wraps the shared HrdLogin component with portal-specific behaviors:
 * - Shows loading screen while MSAL is processing (prevents login form flicker)
 * - Redirects to home/stored path if already authenticated
 * - Passes portal-specific config (authority, domain mappings, menu items)
 */
export function PortalHrdLogin() {
    const { inProgress } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const location = useLocation();

    // While MSAL is processing (handling redirect, etc.), show loading screen
    // This prevents the login form from flickering before redirect
    if (inProgress !== 'none') {
        return <AuthCheckingScreen />;
    }

    // If already authenticated (and not needing re-auth), redirect to the stored post-login path or home
    if (isAuthenticated && !authService.requiresInteraction) {
        const redirectPath = consumePostLoginPath('/');
        return (
            <Navigate
                to={redirectPath}
                replace
            />
        );
    }

    // Don't render if auth is disabled
    if (isAuthDisabled) {
        return (
            <Navigate
                to="/"
                replace
            />
        );
    }

    return (
        <HrdLogin
            currentPath={location.pathname}
            defaultAuthority={defaultAuthority}
            domainMappings={domainMappings}
            storageKeyPrefix={STORAGE_PREFIX}
            hrdHeaderMenu={env.auth.hrdHeaderMenuItems}
            hrdFooterMenu={env.auth.hrdFooterMenuItems}
        />
    );
}
